import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import CustomerHeader from '../../components/CustomerHeader';
import CustomerFooter from '../../components/CustomerFooter';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import {
    HeartHandshake, CheckCircle2, Search, MapPin, ShieldCheck,
    PhoneCall, Truck, BadgePercent, Users, Sparkles, Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import { errMsg } from '../../lib/errors';

export default function Wholesale() {
    const nav = useNavigate();
    const [form, setForm] = useState({
        name: '', mobile: '', email: '', business_name: '', business_type: 'Retailer',
        gstin: '', pan: '', business_address: '', notes: '',
        pincode: '', state: '', district: '', area: '',
    });
    const [postOffices, setPostOffices] = useState([]);
    const [pinLoading, setPinLoading] = useState(false);
    const [selectedPO, setSelectedPO] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);

    const lookupPincode = async () => {
        if (!/^\d{6}$/.test(form.pincode)) {
            toast.error('Enter a valid 6-digit pincode');
            return;
        }
        setPinLoading(true);
        setPostOffices([]);
        setSelectedPO('');
        setForm(f => ({ ...f, state: '', district: '', area: '' }));
        try {
            const r = await api.get(`/shipping/pincode/${form.pincode}`);
            const list = r.data.post_offices || [];
            if (list.length === 0) {
                toast.error(r.data.message || 'No records found for this pincode');
                return;
            }
            setPostOffices(list);
            // Auto-select first if only one
            if (list.length === 1) {
                const p = list[0];
                setSelectedPO(p.post_office);
                setForm(f => ({ ...f, state: p.state, district: p.district, area: p.post_office }));
            } else {
                toast.info(`${list.length} locations found — please select yours`);
            }
        } catch (e) {
            toast.error(errMsg(e, 'Pincode lookup failed'));
        } finally {
            setPinLoading(false);
        }
    };

    const handlePOChange = (poName) => {
        setSelectedPO(poName);
        const p = postOffices.find(x => x.post_office === poName);
        if (p) setForm(f => ({ ...f, state: p.state, district: p.district, area: p.post_office }));
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.state || !form.district || !form.area) {
            toast.error('Please lookup your pincode and select your area');
            return;
        }
        setLoading(true);
        try {
            await api.post('/auth/customer/register-wholesale', {
                name: form.name, mobile: form.mobile, email: form.email || null,
                business_name: form.business_name, business_type: form.business_type,
                gstin: form.gstin, pan: form.pan, business_address: form.business_address,
                notes: form.notes,
                location: {
                    pincode: form.pincode, state: form.state, district: form.district,
                    area: form.area, country: 'India',
                },
            });
            setSubmitted(true);
            toast.success('Application submitted! Our team will contact you shortly.');
        } catch (e) {
            toast.error(errMsg(e, 'Application failed'));
        } finally {
            setLoading(false);
        }
    };

    const benefits = [
        { icon: BadgePercent, title: 'Tiered Volume Pricing', desc: 'Unlock 5 wholesale tiers with progressive discounts as your order volume grows.' },
        { icon: Truck, title: 'Priority Fulfilment', desc: 'Priority packing, dispatch, and freight-forwarding for bulk indent orders.' },
        { icon: Users, title: 'Dedicated Account Manager', desc: 'A single point of contact for quotations, dispatch queries, and support.' },
        { icon: Sparkles, title: 'Early Access & Custom Sourcing', desc: 'First look at new arrivals, seasonal indents, and custom-grown plant orders.' },
    ];

    const approvalSteps = [
        { icon: CheckCircle2, title: 'Submit Application', desc: 'Fill this form with your business & KYC details.' },
        { icon: PhoneCall, title: 'Verification Call', desc: 'Our executive will call you within 1–2 business days to verify your business.' },
        { icon: ShieldCheck, title: 'KYC & Level Assignment', desc: 'Backend admin verifies GSTIN/PAN and assigns a wholesale tier (Level 1–5) based on your purchase capacity.' },
        { icon: Clock, title: 'Account Activation', desc: 'Once approved, sign in with mobile OTP and shop instantly at your assigned wholesale prices.' },
    ];

    return (
        <div className="bg-white min-h-screen">
            <CustomerHeader />

            {/* Hero */}
            <section className="login-hero-bg text-white">
                <div className="max-w-[1400px] mx-auto px-4 py-14">
                    <div className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-medium mb-3">
                        <HeartHandshake className="h-3.5 w-3.5" /> B2B Partner Program
                    </div>
                    <h1 className="text-3xl sm:text-4xl font-bold">Become a Wholesale Partner</h1>
                    <p className="text-white/85 mt-2 max-w-2xl">
                        Grow your nursery, landscaping, or resale business with Dharmik Gardens. Enjoy volume-based pricing, dedicated support, and reliable pan-India logistics.
                    </p>
                </div>
            </section>

            {/* Benefits */}
            <section className="py-12">
                <div className="max-w-[1200px] mx-auto px-4">
                    <div className="text-center mb-8">
                        <h2 className="text-2xl sm:text-3xl font-bold">What you get as a Wholesale Partner</h2>
                        <p className="text-neutral-600 mt-2 max-w-2xl mx-auto">Designed for retailers, nurseries, landscapers, and corporate buyers who need consistent supply and transparent pricing.</p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {benefits.map((b, i) => (
                            <Card key={i} className="p-5 hover:shadow-md transition-shadow" data-testid={`ws-benefit-${i}`}>
                                <div className="h-10 w-10 rounded-lg flex items-center justify-center mb-3" style={{ background: 'hsl(142 76% 92%)' }}>
                                    <b.icon className="h-5 w-5" style={{ color: 'hsl(142 71% 30%)' }} />
                                </div>
                                <div className="font-semibold">{b.title}</div>
                                <div className="text-sm text-neutral-600 mt-1">{b.desc}</div>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* Approval Process */}
            <section className="py-10" style={{ background: 'hsl(142 40% 97%)' }}>
                <div className="max-w-[1200px] mx-auto px-4">
                    <div className="text-center mb-8">
                        <h2 className="text-2xl sm:text-3xl font-bold">Our Approval Process</h2>
                        <p className="text-neutral-600 mt-2 max-w-2xl mx-auto">
                            Every wholesale application is verified by our admin team. A dedicated executive will call you before approval to understand your business and finalise your pricing tier.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {approvalSteps.map((s, i) => (
                            <div key={i} className="relative bg-white rounded-xl border p-5" data-testid={`ws-step-${i}`}>
                                <div className="absolute -top-3 -left-3 h-8 w-8 rounded-full flex items-center justify-center text-white text-sm font-bold" style={{ background: 'hsl(142 71% 45%)' }}>
                                    {i + 1}
                                </div>
                                <s.icon className="h-6 w-6 mb-2" style={{ color: 'hsl(142 71% 30%)' }} />
                                <div className="font-semibold">{s.title}</div>
                                <div className="text-sm text-neutral-600 mt-1">{s.desc}</div>
                            </div>
                        ))}
                    </div>
                    <div className="mt-6 text-xs text-neutral-500 text-center max-w-2xl mx-auto">
                        Note: Your wholesale tier (Level 1–5) is decided by Dharmik Gardens admin based on your business profile, purchase capacity, and order commitment. Higher tiers unlock deeper discounts.
                    </div>
                </div>
            </section>

            {/* Application Form */}
            <section className="py-12">
                <div className="max-w-[900px] mx-auto px-4">
                    {submitted ? (
                        <Card className="p-8 text-center" data-testid="ws-success">
                            <div className="h-16 w-16 rounded-full mx-auto flex items-center justify-center" style={{ background: 'hsl(142 76% 92%)' }}>
                                <CheckCircle2 className="h-9 w-9" style={{ color: 'hsl(142 71% 30%)' }} />
                            </div>
                            <h2 className="text-2xl font-bold mt-4">Application submitted!</h2>
                            <p className="text-neutral-600 mt-2">
                                Thank you for applying. Our admin executive will call you within <strong>1–2 business days</strong> to verify your business details. Once approved, you can sign in using mobile OTP and start shopping at your assigned wholesale prices.
                            </p>
                            <Button className="mt-4" onClick={() => nav('/')} style={{ background: 'hsl(142 71% 45%)', color: 'white' }} data-testid="ws-continue-btn">
                                Continue browsing
                            </Button>
                        </Card>
                    ) : (
                        <Card className="p-6 sm:p-8">
                            <div className="flex items-center gap-3 mb-5">
                                <div className="h-10 w-10 rounded-lg flex items-center justify-center" style={{ background: 'hsl(142 76% 92%)' }}>
                                    <HeartHandshake className="h-5 w-5" style={{ color: 'hsl(142 71% 30%)' }} />
                                </div>
                                <div>
                                    <div className="font-semibold">Wholesale Application</div>
                                    <div className="text-xs text-neutral-500">Fields marked * are required. Approval required to activate wholesale pricing.</div>
                                </div>
                            </div>

                            <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="wholesale-form">
                                <div>
                                    <Label>Contact Name *</Label>
                                    <Input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="ws-name" />
                                </div>
                                <div>
                                    <Label>Mobile *</Label>
                                    <Input required value={form.mobile} onChange={e => setForm({ ...form, mobile: e.target.value })} data-testid="ws-mobile" />
                                </div>
                                <div>
                                    <Label>Email</Label>
                                    <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="ws-email" />
                                </div>
                                <div>
                                    <Label>Business Type *</Label>
                                    <select
                                        value={form.business_type}
                                        onChange={e => setForm({ ...form, business_type: e.target.value })}
                                        className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
                                        data-testid="ws-business-type"
                                    >
                                        <option>Retailer</option>
                                        <option>Nursery</option>
                                        <option>Landscaper</option>
                                        <option>Corporate</option>
                                        <option>Wholesaler</option>
                                        <option>Other</option>
                                    </select>
                                </div>
                                <div className="sm:col-span-2">
                                    <Label>Business Name *</Label>
                                    <Input required value={form.business_name} onChange={e => setForm({ ...form, business_name: e.target.value })} data-testid="ws-business-name" />
                                </div>
                                <div>
                                    <Label>GSTIN</Label>
                                    <Input value={form.gstin} onChange={e => setForm({ ...form, gstin: e.target.value.toUpperCase() })} data-testid="ws-gstin" />
                                </div>
                                <div>
                                    <Label>PAN</Label>
                                    <Input value={form.pan} onChange={e => setForm({ ...form, pan: e.target.value.toUpperCase() })} data-testid="ws-pan" />
                                </div>
                                <div className="sm:col-span-2">
                                    <Label>Business Address</Label>
                                    <Textarea rows={2} value={form.business_address} onChange={e => setForm({ ...form, business_address: e.target.value })} data-testid="ws-address" />
                                </div>

                                {/* Pincode Lookup */}
                                <div className="sm:col-span-2">
                                    <Label>Pincode *</Label>
                                    <div className="flex gap-2">
                                        <Input
                                            value={form.pincode}
                                            onChange={e => {
                                                const v = e.target.value.replace(/\D/g, '').slice(0, 6);
                                                setForm({ ...form, pincode: v });
                                                if (v.length < 6) {
                                                    setPostOffices([]);
                                                    setSelectedPO('');
                                                }
                                            }}
                                            maxLength={6}
                                            placeholder="6-digit pincode"
                                            data-testid="ws-pincode"
                                        />
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={lookupPincode}
                                            disabled={pinLoading || form.pincode.length !== 6}
                                            data-testid="ws-pincode-lookup"
                                        >
                                            <Search className="h-4 w-4 mr-1" />
                                            {pinLoading ? 'Looking up…' : 'Lookup'}
                                        </Button>
                                    </div>
                                </div>

                                {/* Multi Post Office Selector */}
                                {postOffices.length > 1 && (
                                    <div className="sm:col-span-2">
                                        <Label>Select your Area / Post Office *</Label>
                                        <select
                                            value={selectedPO}
                                            onChange={e => handlePOChange(e.target.value)}
                                            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
                                            data-testid="ws-po-select"
                                        >
                                            <option value="">-- Choose from {postOffices.length} matches --</option>
                                            {postOffices.map((p, i) => (
                                                <option key={i} value={p.post_office}>
                                                    {p.post_office} — {p.district}, {p.state}
                                                </option>
                                            ))}
                                        </select>
                                        <div className="text-xs text-neutral-500 mt-1">
                                            Multiple locations share this pincode. Please pick the one nearest to your business.
                                        </div>
                                    </div>
                                )}

                                {/* Resolved location */}
                                {form.state && (
                                    <div className="sm:col-span-2 rounded-lg border bg-neutral-50 p-3 flex items-start gap-2" data-testid="ws-location-preview">
                                        <MapPin className="h-4 w-4 mt-0.5" style={{ color: 'hsl(142 71% 30%)' }} />
                                        <div className="text-sm">
                                            <div className="font-medium">{form.area}</div>
                                            <div className="text-neutral-600">{form.district}, {form.state} — {form.pincode}</div>
                                        </div>
                                    </div>
                                )}

                                <div className="sm:col-span-2">
                                    <Label>Additional Notes</Label>
                                    <Textarea
                                        rows={2}
                                        value={form.notes}
                                        onChange={e => setForm({ ...form, notes: e.target.value })}
                                        placeholder="Tell us about your expected monthly volume, product categories of interest, delivery requirements, etc."
                                        data-testid="ws-notes"
                                    />
                                </div>

                                <div className="sm:col-span-2 rounded-lg p-3 text-xs" style={{ background: 'hsl(142 76% 96%)', color: 'hsl(142 71% 22%)' }}>
                                    By submitting this application you agree to be contacted by our team for business verification. Your wholesale tier will be assigned by our admin team after review — you do not need to select a level yourself.
                                </div>

                                <Button
                                    type="submit"
                                    disabled={loading}
                                    className="sm:col-span-2"
                                    data-testid="ws-submit"
                                    style={{ background: 'hsl(142 71% 45%)', color: 'white' }}
                                >
                                    {loading ? 'Submitting…' : 'Submit Application'}
                                </Button>
                            </form>
                        </Card>
                    )}
                </div>
            </section>

            <CustomerFooter />
        </div>
    );
}
